import { AutoAwesome, Refresh, Landscape } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  kiReadsDocuments,
  type IKiDocumentKind,
  type IKiPosteingangAnalysis,
} from '../../Interfaces/IKi';
import type { IFSScanResult } from '../../Interfaces/IFSDirectory';
import {
  useAnalysePosteingang,
  useGetKiStatus,
} from '../../hooks/reactQuerys/KiQueries';
import { GrundbuchAuszugImportDialog } from '../../Grundstuecke/GrundbuchAuszugImportDialog';

/**
 * Der Vorschlag der KI zu einem Dokument des Posteingangs (#1336).
 *
 * Der Vorschlag wird **nicht** von selbst übernommen. Ein falsch zugeordnetes
 * Dokument hängt danach am falschen Vorgang, und das fällt niemandem mehr auf —
 * die Zuordnung muss deshalb ein Mensch bestätigen. Angezeigt wird immer, worauf
 * der Vorschlag beruht: die gefundene Urkundennummer und die Begründung.
 */

type IProps = {
  fsScanResult: IFSScanResult;
  /**
   * Erst wenn die Vorschau fertig geladen ist, darf untersucht werden.
   * Beide Wege lesen dieselbe Datei über SMB, und `stream_smb_file` schließt
   * am Ende den **globalen** Verbindungs-Pool: gleichzeitig gestartet reißt
   * der eine Aufruf dem anderen die Verbindung weg, und die Vorschau bricht
   * mit „Failed to load PDF file." ab.
   */
  isDocumentReady: boolean;
  /**
   * Das geladene Dokument. Wird gebraucht, wenn dem Schreiben ein
   * Grundbuchausdruck beiliegt: der Import bekommt dieselbe Datei, die hier
   * schon in der Vorschau steht.
   */
  documentBlob?: Blob | null;
  onApply: (suggestion: {
    idLegalTransaction: number;
    section: string;
    idLegalRequirement: number;
  }) => void;
};

/** Wie sicher sich die KI ist — und wie dringend jemand hinsehen sollte. */
const CONFIDENCE_COLOR: Record<string, 'success' | 'warning' | 'default'> = {
  hoch: 'success',
  mittel: 'warning',
  niedrig: 'default',
};

const KIND_HINT: Record<IKiDocumentKind, string> = {
  document: 'Abwicklung / Maßnahmen',
  certificate: 'Urkunden',
  invoice: 'Rechnungen',
  landregister: 'Interne Datenablage',
  unknown: '',
};

export const KiSuggestionPanel = ({
  fsScanResult,
  isDocumentReady,
  documentBlob = null,
  onApply,
}: IProps) => {
  const { kiStatus } = useGetKiStatus();
  const { analysePosteingang, isPendingAnalysePosteingang } =
    useAnalysePosteingang();

  const [analysis, setAnalysis] = useState<IKiPosteingangAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);

  // Merkt sich, welches Dokument bereits untersucht wurde. Ohne das liefe die
  // Untersuchung bei jedem Neuzeichnen erneut — und jede kostet einen
  // Modellaufruf.
  const analysedPath = useRef<string | null>(null);

  const runAnalysis = useCallback(async () => {
    setError(null);
    setAnalysis(null);

    try {
      setAnalysis(
        await analysePosteingang({
          idFSDirectory: fsScanResult.idFSDirectory,
          path: fsScanResult.path,
          fileName: fsScanResult.fileName,
        })
      );
    } catch (exception) {
      setError(
        exception instanceof Error && exception.message
          ? exception.message.replace(/^\d{3} [^:]+: /, '')
          : 'Das Dokument konnte nicht untersucht werden.'
      );
    }
  }, [analysePosteingang, fsScanResult]);

  useEffect(() => {
    if (
      !kiStatus?.Ready ||
      !isDocumentReady ||
      analysedPath.current === fsScanResult.path
    ) {
      return;
    }

    analysedPath.current = fsScanResult.path;
    void runAnalysis();
  }, [kiStatus?.Ready, isDocumentReady, fsScanResult.path, runAnalysis]);

  if (!kiStatus?.Ready || !isDocumentReady) {
    return null;
  }

  if (isPendingAnalysePosteingang) {
    return (
      <Paper
        variant='outlined'
        sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}
      >
        <CircularProgress size={18} />
        <Typography variant='body2'>Das Dokument wird untersucht …</Typography>
      </Paper>
    );
  }

  if (error !== null) {
    return (
      <Alert
        severity='info'
        sx={{ mb: 2 }}
        action={
          <Button size='small' startIcon={<Refresh />} onClick={runAnalysis}>
            Erneut
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (analysis === null) {
    return null;
  }

  const canApply =
    analysis.idLegalTransaction !== null && analysis.Section !== null;

  return (
    <Alert
      icon={<AutoAwesome fontSize='inherit' />}
      severity={canApply ? 'success' : 'info'}
      variant='outlined'
      sx={{ mb: 2 }}
      action={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size='small' startIcon={<Refresh />} onClick={runAnalysis}>
            Erneut
          </Button>
          {analysis.LandRegisterEnclosed && kiReadsDocuments(kiStatus) && (
            // Getrennt vom Übernehmen: das Schreiben gehört an eine Maßnahme,
            // die beiliegende Ausfertigung wird ein Grundbuchblatt. Beides
            // gehört zusammen, aber keines ersetzt das andere.
            // Die Zuordnung kommt mit Text aus, das Auslesen nicht — kann der
            // eingestellte Anbieter keine Datei lesen, fehlt nur dieser Knopf.
            <Button
              size='small'
              variant='outlined'
              startIcon={<Landscape />}
              disabled={documentBlob === null}
              onClick={() => setIsImportOpen(true)}
            >
              Grundbuch übernehmen
            </Button>
          )}
          {canApply && (
            <Button
              size='small'
              variant='contained'
              onClick={() =>
                onApply({
                  idLegalTransaction: Number(analysis.idLegalTransaction),
                  section: String(analysis.Section),
                  // -1 heißt „nichts gewählt“ — dieselbe Schreibweise wie im
                  // leeren Auswahlobjekt des Dialogs.
                  idLegalRequirement: analysis.idLegalRequirement ?? -1,
                })
              }
            >
              Übernehmen
            </Button>
          )}
        </Box>
      }
    >
      <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {analysis.KindLabel}
        {analysis.Confidence !== null && (
          <Chip
            size='small'
            label={`Sicherheit: ${analysis.Confidence}`}
            color={CONFIDENCE_COLOR[analysis.Confidence] ?? 'default'}
          />
        )}
        {KIND_HINT[analysis.Kind] !== '' && (
          <Typography variant='caption' color='text.secondary'>
            gehört nach „{KIND_HINT[analysis.Kind]}"
          </Typography>
        )}
      </AlertTitle>

      {analysis.idLegalTransaction === null ? (
        <Typography variant='body2'>
          Es konnte kein passender Vorgang gefunden werden. Bitte von Hand
          zuordnen.
        </Typography>
      ) : (
        <Typography variant='body2'>
          <b>Vorgang:</b> {analysis.LegalTransactionTitle} (Nr.{' '}
          {analysis.idLegalTransaction})
          {analysis.LegalRequirement !== null && (
            <>
              <br />
              <b>Maßnahme:</b> {analysis.LegalRequirement}
            </>
          )}
        </Typography>
      )}

      {analysis.RecordNumberArray.length > 0 && (
        <Typography variant='caption' sx={{ display: 'block', mt: 0.5 }}>
          Gefundene Urkundennummer: {analysis.RecordNumberArray.join(', ')}
        </Typography>
      )}

      {analysis.Reason !== null && (
        <Typography
          variant='caption'
          sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
        >
          {analysis.Reason}
        </Typography>
      )}

      {analysis.LandRegisterEnclosed && (
        <Typography
          variant='caption'
          sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}
        >
          {kiReadsDocuments(kiStatus)
            ? 'Dem Schreiben liegt ein Grundbuchausdruck bei — er lässt sich als Grundbuchblatt übernehmen, unabhängig von der Zuordnung.'
            : 'Dem Schreiben liegt ein Grundbuchausdruck bei. Der eingestellte KI-Anbieter kann ihn nicht auslesen; bitte von Hand übertragen.'}
        </Typography>
      )}

      {isImportOpen && (
        // Derselbe Dialog wie auf „Grundstücke", nur mit der Datei aus dem
        // Posteingang statt einer Auswahl — eine Prüfstation, ein Editor.
        <GrundbuchAuszugImportDialog
          open={isImportOpen}
          onClose={() => setIsImportOpen(false)}
          initialFile={
            documentBlob === null
              ? null
              : new File([documentBlob], fsScanResult.fileName, {
                  type: 'application/pdf',
                })
          }
        />
      )}
    </Alert>
  );
};
